"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CompanyOnboardingFileUploadHeader = CompanyOnboardingFileUploadHeader;
exports.default = CompanyOnboardingFileUpload;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_config_1 = __importDefault(require("react-native-config"));
const react_native_1 = require("react-native");
const react_i18next_1 = require("react-i18next");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
const SessionContext_1 = require("../contexts/SessionContext");
const UserContext_1 = require("../contexts/UserContext");
const ProspectContext_1 = require("../contexts/ProspectContext");
const CompanyOnboarding_1 = __importDefault(require("../api/CompanyOnboarding"));
const Button_1 = __importDefault(require("../components/Button"));
const ModalWrapper_1 = __importDefault(require("../components/ModalWrapper"));
const FileUploadList_1 = __importDefault(require("../partials/FileUploadList"));
const FirebaseAnalytics_1 = __importDefault(require("../shared/FirebaseAnalytics"));
const Files_1 = require("../shared/Files");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
function CompanyOnboardingFileUploadHeader(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    return ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: props.icon ?? Theme_1.ICONS.DuoTone.FileImport, size: 20, color: THEME.Secondary, style: { pointerEvents: 'none' } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1 }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontWeight: '500', fontSize: size != 'sm' ? 18 : 16, marginLeft: 12 }, children: props.title }) }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginBottom: -4, marginRight: -12 }, children: (0, jsx_runtime_1.jsx)(Button_1.default, { "aria-label": t('common:buttons.close'), hitSlop: 12, type: 'borderless', onPress: props.onClose, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.Xmark, size: 20, color: THEME.Text, style: { margin: 4, pointerEvents: 'none' } }) }) })] }));
}
function CompanyOnboardingFileUpload(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const [uploaded, setUploaded] = react_1.default.useState(false);
    const sessionContext = react_1.default.useContext(SessionContext_1.SessionContext);
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const prospectContext = react_1.default.useContext(ProspectContext_1.ProspectContext);
    const dynamicContext = react_1.default.useMemo(() => {
        switch (sessionContext.type) {
            case SessionContext_1.SessionType.Employee: return userContext;
            case SessionContext_1.SessionType.Prospect: return prospectContext;
            default: return sessionContext;
        }
    }, [sessionContext.type]);
    const uploadTypes = {
        Census: {
            filePrefix: "Census",
            filename: t('company:labels.census'),
            icon: Theme_1.ICONS.DuoTone.FileUser,
            templateName: "EmployeeCensus.xlsx",
            templateType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            extensions: ['xlsx', 'csv'],
            mimeTypes: [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ],
            utiTypes: [
                'org.openxmlformats.spreadsheetml.sheet',
                'public.comma-separated-values-text'
            ]
        },
        TimeQuestionnaire: { filePrefix: "TimeQuestionnaire", filename: "Time Questionnaire", icon: Theme_1.ICONS.DuoTone.ClipboardQuestion },
        PtoBalances: { filePrefix: "PtoBalances", filename: "Pto Balances", icon: Theme_1.ICONS.DuoTone.IslandTropical },
        PtoPolicy: {
            filePrefix: "PtoPolicy",
            filename: t('company:labels.ptoPolicy'),
            icon: Theme_1.ICONS.DuoTone.FolderGear,
            extensions: ['pdf', 'doc', 'docx'],
            mimeTypes: [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ],
            utiTypes: [
                'com.adobe.pdf',
                'com.microsoft.word.doc',
                'org.openxmlformats.wordprocessingml.document'
            ]
        },
        Retirement: {
            filePrefix: "Retirement",
            filename: "Plan Information",
            icon: Theme_1.ICONS.DuoTone.PiggyBank,
            description: t('company:text.retirementUpload')
        },
        RetirementContributions: {
            filePrefix: "RetirementContributions",
            filename: t('company:labels.employeeContributions'),
            icon: Theme_1.ICONS.DuoTone.ChartPieSimpleCircleDollar,
            description: t('company:text.retirementContributionsUpload')
        },
        RetirementLoans: { filePrefix: "RetirementLoans", filename: t('company:labels.loans'), icon: Theme_1.ICONS.DuoTone.SackDollar },
        LossRun: {
            filePrefix: "LossRun",
            filename: t('company:labels.lossRun'),
            icon: Theme_1.ICONS.DuoTone.ClipboardMedical,
            description: t('company:text.lossRunUpload')
        },
        ProofOfCoverage: {
            filePrefix: "ProofOfCoverage",
            filename: t('company:labels.coverageProof'),
            icon: Theme_1.ICONS.DuoTone.FileCertificate,
            description: t('company:text.proofOfCoverageUpload')
        },
        Cobra: { filePrefix: "Cobra", filename: t('company:labels.cobra'), icon: Theme_1.ICONS.DuoTone.FilesMedical },
        GeneralLiability: {
            filePrefix: "GeneralLiability",
            filename: t('company:labels.generalLiability'),
            icon: Theme_1.ICONS.DuoTone.CarBurst,
            description: t('company:text.generalLiabilityUpload')
        },
        OtherBenefits: {
            filePrefix: "OtherBenefits",
            filename: t('company:labels.otherBenefits'),
            icon: Theme_1.ICONS.DuoTone.HandshakeAngle,
            description: t('company:text.otherBenefitsUpload')
        },
        Health: {
            filePrefix: "Health",
            filename: t('company:labels.healthBenefits'),
            icon: Theme_1.ICONS.DuoTone.HeartPulse,
            description: t('company:text.healthUpload')
        },
        Dental: {
            filePrefix: "Dental",
            filename: t('company:labels.dentalBenefits'),
            icon: Theme_1.ICONS.DuoTone.Tooth,
            description: t('company:text.dentalUpload')
        },
        Vision: {
            filePrefix: "Vision",
            filename: t('company:labels.visionBenefits'),
            icon: Theme_1.ICONS.DuoTone.Glasses,
            description: t('company:text.visionUpload')
        },
        ChartOfAccounts: {
            filePrefix: "ChartOfAccounts",
            filename: t('company:labels.chartOfAccounts'),
            icon: Theme_1.ICONS.DuoTone.FileInvoiceDollar,
            description: t('company:text.chartOfAccountsUpload')
        },
        Garnishment: {
            filePrefix: "Garnishment",
            filename: t('company:labels.garnishments'),
            icon: Theme_1.ICONS.DuoTone.Gavel,
            description: t('company:text.garnishmentsUpload')
        },
        PreviousPayroll: {
            filePrefix: "PreviousPayroll",
            filename: t('company:labels.previousPayroll'),
            icon: Theme_1.ICONS.DuoTone.MoneyCheckDollar,
            description: t('company:text.previousPayrollUpload')
        },
        YtdWageReport: {
            filePrefix: "YtdWageReport",
            filename: t('company:labels.ytdWages'),
            icon: Theme_1.ICONS.DuoTone.MoneyCheckDollar,
            description: t('company:text.ytdWagesUpload')
        },
        LegalBusinessLetter: {
            filePrefix: "LegalBusinessLetter",
            filename: t('company:labels.legalBusinessLetter'),
            icon: Theme_1.ICONS.DuoTone.EnvelopeOpenText,
            description: t('company:text.legalBusinessLetterUpload')
        },
        EPLIPolicy: {
            filePrefix: "EPLIPolicy",
            filename: "EPLI Policy",
            icon: Theme_1.ICONS.DuoTone.FileShield,
            description: t('company:text.EPLIPolicyUpload')
        },
        Other: {
            filePrefix: "Other",
            filename: t('company:labels.other'),
            icon: Theme_1.ICONS.DuoTone.FileImport,
            description: t('company:text.otherUpload')
        },
        MedicalCensus: {
            filePrefix: "MedicalCensus",
            filename: t('company:labels.medicalCensus'),
            icon: Theme_1.ICONS.DuoTone.FileUser,
            templateName: "MedicalCensus.xlsx",
            templateType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            extensions: ['xlsx', 'csv'],
            mimeTypes: [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ],
            utiTypes: [
                'org.openxmlformats.spreadsheetml.sheet',
                'public.comma-separated-values-text'
            ]
        },
        SutaRateNotice: {
            filePrefix: "SutaRateNotice",
            filename: t('company:labels.sutaRateNotice'),
            icon: Theme_1.ICONS.DuoTone.MoneyCheckDollar,
            description: t('company:text.sutaRateNoticeUpload')
        },
        MedicalBilling: {
            filePrefix: "MedicalBilling",
            filename: t('company:labels.medicalBilling'),
            icon: Theme_1.ICONS.DuoTone.FilesMedical,
            description: t('company:text.medicalBillingUpload')
        },
        BenefitSummary: {
            filePrefix: "BenefitSummary",
            filename: t('company:labels.benefitSummary'),
            icon: Theme_1.ICONS.DuoTone.HeartPulse,
            description: t('company:text.benefitSummaryUpload')
        },
        WcDeclaration: {
            filePrefix: "WcDeclaration",
            filename: t('company:labels.wcDeclaration'),
            icon: Theme_1.ICONS.DuoTone.Gavel,
            description: t('company:text.wcDeclarationUpload')
        },
    };
    const uploadType = uploadTypes[props.route.params.type];
    return ((0, jsx_runtime_1.jsxs)(ModalWrapper_1.default, { navigation: props.navigation, onClose: () => {
            if (uploaded)
                react_native_1.DeviceEventEmitter.emit("event.refreshOnboarding");
            props.navigation.goBack();
        }, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { paddingBottom: 8 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginHorizontal: 12 }, children: (0, jsx_runtime_1.jsx)(CompanyOnboardingFileUploadHeader, { title: uploadType.filename, icon: uploadType.icon, onClose: () => {
                                if (uploaded)
                                    react_native_1.DeviceEventEmitter.emit("event.refreshOnboarding");
                                props.navigation.goBack();
                            } }) }), !!uploadType.description &&
                        (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginHorizontal: 12, marginVertical: 8 }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { fontSize: 14, color: THEME.Text }, children: uploadType.description }) }), !!uploadType.templateName &&
                        (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { marginHorizontal: 12, marginVertical: 8, padding: 12, borderColor: THEME.LightGray, borderRadius: 4, borderWidth: 1 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginBottom: 12 }, children: (0, jsx_runtime_1.jsxs)(react_native_1.Text, { style: { color: THEME.Text, fontSize: 16 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { fontWeight: '500' }, children: "1" }), "\u00A0\u2013\u00A0", t('company:text.uploadTemplate')] }) }), (0, jsx_runtime_1.jsxs)(Button_1.default, { type: "borderless", onPress: () => {
                                        FirebaseAnalytics_1.default.LogEvent('feature_used', {
                                            'feature': 'Download Company Onboarding Template',
                                            'client': userContext?.client?.id,
                                            'employee': userContext?.employee?.employeeId
                                        });
                                        Files_1.Files.SaveToDownloads(uploadType.templateName, uploadType.templateType, react_native_config_1.default.PUBLIC_STORAGE_URL + "/" + uploadType.templateName);
                                    }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Light.FileLines, size: 20, style: { marginRight: 6, marginTop: 2, pointerEvents: 'none' } }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Primary, fontSize: 16, fontWeight: '500', textDecorationLine: 'underline' }, children: uploadType.templateName })] })] })] }), (0, jsx_runtime_1.jsxs)(react_native_1.ScrollView, { contentContainerStyle: {
                    paddingHorizontal: 12,
                    paddingBottom: 12,
                    borderColor: THEME.LightGray,
                    borderRadius: 4,
                    borderWidth: 1
                }, keyboardShouldPersistTaps: 'handled', children: [!!uploadType.templateName &&
                        (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginBottom: 12 }, children: (0, jsx_runtime_1.jsxs)(react_native_1.Text, { style: { color: THEME.Text, fontSize: 16 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { fontWeight: '500' }, children: "2" }), "\u00A0\u2013\u00A0", t('company:text.censusUpload')] }) }), (0, jsx_runtime_1.jsx)(FileUploadList_1.default, { disabled: userContext?.isImpersonated, ...(uploadType.extensions ? { extensions: uploadType.extensions } : {}), ...(uploadType.mimeTypes ? { mimeTypes: uploadType.mimeTypes } : {}), ...(uploadType.utiTypes ? { utiTypes: uploadType.utiTypes } : {}), getFileList: () => dynamicContext.getToken().then(token => CompanyOnboarding_1.default.listUploads(token, uploadType.filePrefix).then(d => d.map((u, i) => ({
                            name: u.filename ?? `${uploadType.filename} (${i + 1})`,
                            date: u.readableDate,
                            type: u.readableType,
                            size: u.readableSize
                        })))), getUploadUrl: (type, name) => {
                            FirebaseAnalytics_1.default.LogEvent('feature_used', {
                                'feature': `Upload Company Onboarding File - ${uploadType.filename}`,
                                'client': userContext?.client?.id,
                                'employee': userContext?.employee?.employeeId
                            });
                            setUploaded(true);
                            return dynamicContext.getToken().then(token => CompanyOnboarding_1.default.startUpload(token, uploadType.filePrefix, type, name));
                        } })] })] }));
}
